import React from "react";
import styled, { useTheme } from "styled-components";
import { TriangleAlert } from "lucide-react";
import { TradeData } from "@shared/types";
import { directionColor, getNewLookTokens } from "common/NewLook/tokens.ts";
import PriceLadder from "common/NewLook/PriceLadder.tsx";
import { isTradeBarModel } from "common/NewLook/tradeBarLayout.ts";
import { round } from "@shared/utils/numbers";
import { usePnlSocketData } from "features/trades/TradesCards/PnlSocketDataProvider.tsx";
import {
  formatTimeInTrade,
  useLadderPriceFormat,
  useTradeRowLadder,
} from "features/trades/NewLook/useTradeLadder.ts";
import { useLadderCaption } from "features/trades/NewLook/useLadderCaption.ts";
import { formatSignedPercent } from "features/trades/NewLook/useMoneyFormat.ts";
import { NEW_LOOK_COMPACT_TABLE_CLASS } from "features/trades/common/constants";

interface NewLookProgressCellProps {
  tradeData: TradeData;
  /** The live row from the PnL socket, when there is one. */
  liveData?: Partial<TradeData>;
  isOpenTrades: boolean;
}

/**
 * Trades table progress column: the compact price ladder over a one-line
 * caption (what is filled, and the time in trade).
 */
const NewLookProgressCell = ({
  tradeData,
  liveData,
  isOpenTrades,
}: NewLookProgressCellProps) => {
  const model = useTradeRowLadder(tradeData, liveData, isOpenTrades);
  const formatPrice = useLadderPriceFormat(
    tradeData.symbol,
    tradeData.exchange
  );
  const caption = useLadderCaption(model, formatPrice);
  const hasTradeBar = model.type === "levels" && isTradeBarModel(model);
  const timePassed = formatTimeInTrade(
    (liveData?.graph_data || tradeData.graph_data)?.time_passed
  );
  return (
    <Cell $hasTradeBar={hasTradeBar}>
      <PriceLadder model={model} formatPrice={formatPrice} />
      {!hasTradeBar && (
        <Caption>
          <CaptionText $isWarning={caption.isWarning}>
            {caption.isWarning && <TriangleAlert size={11} strokeWidth={2} />}
            <span>{caption.text}</span>
          </CaptionText>
          {timePassed && <Time>{timePassed}</Time>}
        </Caption>
      )}
    </Cell>
  );
};

export default React.memo(NewLookProgressCell);

interface NewLookCardProgressProps {
  tradeData: TradeData;
  isOpenTrades?: boolean;
}

/**
 * Phone / cards-view trade row: the live PnL % and the time in trade over the
 * compact price ladder.
 */
export const NewLookCardProgress = React.memo(
  ({ tradeData, isOpenTrades = true }: NewLookCardProgressProps) => {
    const theme = useTheme();
    const socketData = usePnlSocketData() as Partial<TradeData> | undefined;
    const model = useTradeRowLadder(tradeData, socketData, isOpenTrades);
    const formatPrice = useLadderPriceFormat(
      tradeData.symbol,
      tradeData.exchange
    );
    const graphData = socketData?.graph_data || tradeData.graph_data;
    const profit =
      graphData && "profit" in graphData
        ? round((graphData as { profit: number }).profit || 0, 2)
        : undefined;
    return (
      <CardCell>
        <CardTop>
          {profit !== undefined ? (
            <CardPercent
              style={{
                color: directionColor(getNewLookTokens(theme), profit),
              }}
            >
              {formatSignedPercent(profit)}
            </CardPercent>
          ) : (
            <span />
          )}
          {graphData?.time_passed && (
            <Time>{formatTimeInTrade(graphData.time_passed)}</Time>
          )}
        </CardTop>
        <PriceLadder model={model} formatPrice={formatPrice} />
      </CardCell>
    );
  }
);

const Cell = styled.div<{ $hasTradeBar: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  width: ${(p) => (p.$hasTradeBar ? 256 : 164)}px;
  max-width: 100%;
  @container nl-trades (min-width: 1300px) {
    width: ${(p) => (p.$hasTradeBar ? 300 : 220)}px;
  }
  .${NEW_LOOK_COMPACT_TABLE_CLASS} & {
    width: ${(p) => (p.$hasTradeBar ? 256 : 128)}px;
  }
`;
const Caption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
  font-size: 11px;
  line-height: 14px;
  font-weight: ${(p) => p.theme.typography.fontWeights.NORMAL};
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const CaptionText = styled.span<{ $isWarning?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  color: ${(p) => (p.$isWarning ? p.theme.colors.orange : "inherit")};
  svg {
    flex: none;
  }
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
const Time = styled.span`
  flex: none;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  font-size: 11px;
  line-height: 14px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
// Leaves room for the card's actions button on the right.
const CardCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding-right: 4px;
`;
const CardPercent = styled.span`
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
const CardTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px;
  flex-wrap: wrap;
  line-height: 18px;
`;
